import nhStyleFingerprintFixture from '../data/nhStyleFingerprintSnapshot.json';

export { nhStyleFingerprintFixture };

export const NH_STYLE_FINGERPRINT_CAPTION =
  'Observed portfolio, not complete history. Matches are similar style portfolios, not flavor or quality.';

export const NO_COMPLETED_SNAPSHOT_MESSAGE = 'No completed snapshot is loaded.';

export const NO_IDENTITIES_MATCH_MESSAGE = 'No identities match.';

export const NO_MATCHES_MESSAGE = 'No matches';

const BREWERY_IDENTITY_PREFIX = 'brewery:';

const BREWERY_GROUPS_PATH = '/brewery-groups';

const LARGEST_SHARE_GAPS_MARKER = 'Largest share gaps:';

/** Four-decimal total variation above this is omitted from the profile list. */
const SIMILAR_VARIATION_LIMIT_E4 = 3000;

const FAILED_SNAPSHOT_MESSAGE = 'NH style fingerprint snapshot failed its checks.';

const INCOMPLETE_SNAPSHOT_MESSAGE = 'NH style fingerprint snapshot is incomplete.';

/** Preferred style-group order. Unmapped is after every named group. */
const STYLE_GROUP_ORDER = [
  'Amber & Red',
  'Belgian Ales',
  'Blonde & Golden Ales',
  'Bocks',
  'Brown Ale',
  'Dark Ales',
  'Farmhouse Ales',
  'Fruit & Spice Beers',
  'India Pale Ales (IPA)',
  'Lagers',
  'Non-Alcoholic',
  'Pale Ales',
  'Pilsners',
  'Porters',
  'Sour & Wild Beers',
  'Specialty Beers',
  'Stouts',
  'Wheat Beers',
  'Other Styles',
];

const UNMAPPED_GROUP = 'Unmapped';

/**
 * @param {unknown} source
 * @returns {{ snapshot: object | null, error: string | null }}
 */
export function loadNhStyleFingerprintSnapshot(source) {
  if (source == null) {
    return { snapshot: null, error: null };
  }
  if (typeof source !== 'object') {
    return { snapshot: null, error: INCOMPLETE_SNAPSHOT_MESSAGE };
  }
  if (source.checks?.passed !== true) {
    return { snapshot: null, error: FAILED_SNAPSHOT_MESSAGE };
  }
  if (!source.overview || typeof source.overview !== 'object' || !Array.isArray(source.identities)) {
    return { snapshot: null, error: INCOMPLETE_SNAPSHOT_MESSAGE };
  }
  return { snapshot: source, error: null };
}

/**
 * @param {string} group
 * @returns {number}
 */
function styleGroupRank(group) {
  if (group === UNMAPPED_GROUP) {
    return STYLE_GROUP_ORDER.length + 1;
  }
  const index = STYLE_GROUP_ORDER.indexOf(group);
  if (index === -1) {
    return STYLE_GROUP_ORDER.length;
  }
  return index;
}

/**
 * @param {number} tenths
 * @returns {number}
 */
export function displayPercentFromTenths(tenths) {
  if (!Number.isFinite(tenths)) {
    return 0;
  }
  return tenths / 10;
}

/**
 * @param {number} tenths
 * @returns {number}
 */
export function linearProgressValue(tenths) {
  const percent = displayPercentFromTenths(tenths);
  return Math.min(100, Math.max(0, percent));
}

/**
 * Track width for one style bar, scaled so the largest displayed share fills the track.
 * A lone group at 100% stays at 100, the same as an unscaled bar.
 * @param {number} tenths
 * @param {number} maxTenths
 * @returns {number}
 */
export function relativeStyleBarValue(tenths, maxTenths) {
  const share = displayPercentFromTenths(tenths);
  const maxShare = displayPercentFromTenths(maxTenths);
  if (maxShare <= 0) {
    return 0;
  }
  const scaled = (share / maxShare) * 100;
  return Math.min(100, Math.max(0, scaled));
}

/**
 * @param {number} tenths
 * @returns {string}
 */
export function formatDisplayPercent(tenths) {
  return `${displayPercentFromTenths(tenths).toFixed(1)}%`;
}

/**
 * @param {number} totalVariationE10
 * @returns {string | null}
 */
export function formatTotalVariation(totalVariationE10) {
  if (!Number.isFinite(totalVariationE10)) {
    return null;
  }
  const fourDecimals = Math.round(totalVariationE10 / 1e6) / 10000;
  return fourDecimals.toFixed(4);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function formatCount(value) {
  if (!Number.isFinite(value)) {
    return 'unknown';
  }
  return String(value);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatMappingCoverage(value) {
  if (!Number.isFinite(value)) {
    return 'unknown';
  }
  if (value >= 0 && value <= 1) {
    return `${value} (${(value * 100).toFixed(1)}%)`;
  }
  return String(value);
}

/**
 * @param {unknown} balanced
 * @returns {string}
 */
export function formatReconciliation(balanced) {
  if (balanced === true) {
    return 'balanced';
  }
  if (balanced === false) {
    return 'not balanced';
  }
  return 'unknown';
}

/**
 * @param {object | null | undefined} snapshot
 * @returns {{ label: string, value: string }[]}
 */
export function readOverviewLines(snapshot) {
  const overview = snapshot?.overview ?? {};
  return [
    { label: 'Packaged', value: typeof snapshot?.packagedAt === 'string' ? snapshot.packagedAt : 'unknown' },
    {
      label: 'Calculation version',
      value: typeof snapshot?.calculationVersion === 'string' ? snapshot.calculationVersion : 'unknown',
    },
    { label: 'NH locations', value: formatCount(overview.nhBreweryLocations) },
    { label: 'Identities', value: formatCount(overview.identities) },
    { label: 'Included beers', value: formatCount(overview.includedBeers) },
    { label: 'Mapping coverage', value: formatMappingCoverage(overview.mappingCoverage) },
    { label: 'Eligible', value: formatCount(overview.eligible) },
    { label: 'Ineligible', value: formatCount(overview.ineligible) },
    { label: 'Reconciliation', value: formatReconciliation(overview.reconciliationBalanced) },
  ];
}

/**
 * @param {object[] | null | undefined} identities
 * @param {{ nameQuery?: string, eligibility?: string }} [criteria]
 * @returns {object[]}
 */
export function filterIdentities(identities, criteria = {}) {
  const rows = Array.isArray(identities) ? identities : [];
  const nameQuery = typeof criteria.nameQuery === 'string' ? criteria.nameQuery : '';
  const eligibility = criteria.eligibility === 'eligible' || criteria.eligibility === 'ineligible'
    ? criteria.eligibility
    : 'all';
  const normalizedName = nameQuery.trim().toLowerCase();
  return rows.filter((identity) => {
    if (eligibility === 'eligible' && identity?.matchEligible !== true) {
      return false;
    }
    if (eligibility === 'ineligible' && identity?.matchEligible === true) {
      return false;
    }
    if (normalizedName.length === 0) {
      return true;
    }
    const displayName = typeof identity?.displayName === 'string' ? identity.displayName : '';
    return displayName.toLowerCase().includes(normalizedName);
  });
}

/**
 * @param {object | null | undefined} snapshot
 * @param {string} identityId
 * @returns {object | null}
 */
export function findIdentity(snapshot, identityId) {
  if (!snapshot || typeof identityId !== 'string' || identityId.length === 0) {
    return null;
  }
  if (!Array.isArray(snapshot.identities)) {
    return null;
  }
  return snapshot.identities.find((identity) => identity.identityId === identityId) ?? null;
}

/**
 * @param {string} identityId
 * @returns {string}
 */
export function nhStyleFingerprintPath(identityId) {
  return `/nh-style-fingerprints/${encodeURIComponent(identityId)}`;
}

/**
 * @param {string} breweryId
 * @returns {string}
 */
export function breweryPagePath(breweryId) {
  return `/brewery/${encodeURIComponent(breweryId)}`;
}

/**
 * @param {object | null | undefined} identity
 * @returns {string | null}
 */
export function ungroupedBreweryId(identity) {
  if (!identity || identity.kind === 'group') {
    return null;
  }
  const identityId = identity.identityId;
  if (typeof identityId !== 'string' || !identityId.startsWith(BREWERY_IDENTITY_PREFIX)) {
    return null;
  }
  const breweryId = identityId.slice(BREWERY_IDENTITY_PREFIX.length);
  return breweryId.length > 0 ? breweryId : null;
}

/**
 * @param {object | null | undefined} identity
 * @returns {{ id: string, name: string, path: string }[]}
 */
export function memberBreweryLinks(identity) {
  const ids = Array.isArray(identity?.memberBreweryIds) ? identity.memberBreweryIds : [];
  const names = identity?.memberBreweryNames && typeof identity.memberBreweryNames === 'object'
    ? identity.memberBreweryNames
    : {};
  return ids
    .filter((breweryId) => typeof breweryId === 'string' && breweryId.length > 0)
    .map((breweryId) => {
      const storedName = names[breweryId];
      const name = typeof storedName === 'string' && storedName.trim().length > 0
        ? storedName.trim()
        : breweryId;
      return {
        id: breweryId,
        name,
        path: breweryPagePath(breweryId),
      };
    });
}

/**
 * @param {object | null | undefined} identity
 * @returns {string}
 */
export function formatEligibility(identity) {
  if (identity?.matchEligible === true) {
    return 'eligible';
  }
  const reasons = Array.isArray(identity?.ineligibleReasons) ? identity.ineligibleReasons : [];
  const labels = reasons.filter((reason) => typeof reason === 'string' && reason.length > 0);
  if (labels.length === 0) {
    return 'ineligible';
  }
  return labels.join(', ');
}

/**
 * @param {object[] | null | undefined} leadingFocus
 * @returns {string}
 */
export function formatLeadingFocus(leadingFocus) {
  if (!Array.isArray(leadingFocus) || leadingFocus.length === 0) {
    return 'none';
  }
  return leadingFocus
    .map((entry) => `${entry.group} ${entry.count} (${entry.displayPercentTenths} tenths)`)
    .join('; ');
}

const LEADING_FOCUS_SLOT_INDEXES = [0, 1, 2];

/**
 * @param {object | null | undefined} entry
 * @returns {string}
 */
function formatLeadingFocusCell(entry) {
  if (!entry || typeof entry.group !== 'string') {
    return '';
  }
  const group = entry.group.trim();
  if (group.length === 0) {
    return '';
  }
  return `${group} ${formatDisplayPercent(entry.displayPercentTenths)}`;
}

/**
 * Three leading-focus cells in stored order. A missing slot is blank.
 * @param {object[] | null | undefined} leadingFocus
 * @returns {string[]}
 */
export function formatLeadingFocusCells(leadingFocus) {
  const entries = Array.isArray(leadingFocus) ? leadingFocus : [];
  return LEADING_FOCUS_SLOT_INDEXES.map((slot) => formatLeadingFocusCell(entries[slot]));
}

/**
 * @param {object | null | undefined} identity
 * @returns {string}
 */
export function formatTopMatch(identity) {
  const matches = Array.isArray(identity?.matches) ? identity.matches : [];
  const topMatch = matches[0];
  if (!topMatch || typeof topMatch.displayName !== 'string' || topMatch.displayName.length === 0) {
    return 'none';
  }
  const variation = formatTotalVariation(topMatch.totalVariationE10);
  if (!variation) {
    return topMatch.displayName;
  }
  return `${topMatch.displayName} — ${variation}`;
}

/**
 * @param {object | null | undefined} identity
 * @returns {string}
 */
export function formatCoverageNote(identity) {
  const note = typeof identity?.catalogNote === 'string' ? identity.catalogNote.trim() : '';
  const reasons = Array.isArray(identity?.ineligibleReasons) ? identity.ineligibleReasons : [];
  const labels = reasons.filter((reason) => typeof reason === 'string' && reason.length > 0);
  if (labels.length === 0) {
    return note;
  }
  const reasonText = labels.join(', ');
  if (note.length === 0) {
    return reasonText;
  }
  return `${note} ${reasonText}`;
}

/**
 * @param {object | null | undefined} identity
 * @returns {{ group: string, count: number, displayPercentTenths: number, gapTenths: number | null }[]}
 */
export function styleBarsForIdentity(identity) {
  const rows = Array.isArray(identity?.styleGroups) ? identity.styleGroups : [];
  const gaps = new Map();
  const distinctiveFocus = Array.isArray(identity?.distinctiveFocus) ? identity.distinctiveFocus : [];
  distinctiveFocus.forEach((entry) => {
    if (entry && typeof entry.group === 'string') {
      gaps.set(entry.group, entry.gapTenths);
    }
  });
  return rows
    .filter((row) => row && typeof row.group === 'string' && Number(row.count) > 0)
    .map((row) => ({
      group: row.group,
      count: row.count,
      displayPercentTenths: row.displayPercentTenths,
      gapTenths: gaps.has(row.group) && Number.isFinite(gaps.get(row.group)) ? gaps.get(row.group) : null,
    }))
    .sort((left, right) => {
      const rankDifference = styleGroupRank(left.group) - styleGroupRank(right.group);
      if (rankDifference !== 0) {
        return rankDifference;
      }
      return left.group.localeCompare(right.group);
    });
}

const DISTINCTIVE_STYLE_SLOT_INDEXES = [0, 1, 2];

/**
 * @param {{ group?: string, displayPercentTenths?: number } | undefined} bar
 * @returns {string}
 */
function formatDistinctiveStyleCell(bar) {
  if (!bar || typeof bar.group !== 'string') {
    return '';
  }
  const group = bar.group.trim();
  if (group.length === 0) {
    return '';
  }
  return `${group} ${formatDisplayPercent(bar.displayPercentTenths)}`;
}

/**
 * Largest positive gap first. Equal gaps keep detail-page style order.
 * @param {{ group: string, gapTenths: number }} left
 * @param {{ group: string, gapTenths: number }} right
 * @returns {number}
 */
function compareDistinctiveGap(left, right) {
  const gapDifference = right.gapTenths - left.gapTenths;
  if (gapDifference !== 0) {
    return gapDifference;
  }
  const rankDifference = styleGroupRank(left.group) - styleGroupRank(right.group);
  if (rankDifference !== 0) {
    return rankDifference;
  }
  return left.group.localeCompare(right.group);
}

/**
 * A listed distinctive style has a positive finite gap and a non-blank name.
 * @param {{ group?: unknown, gapTenths?: number } | null | undefined} bar
 * @returns {boolean}
 */
function isListedDistinctiveStyle(bar) {
  if (!bar || typeof bar.group !== 'string' || bar.group.trim().length === 0) {
    return false;
  }
  return Number.isFinite(bar.gapTenths) && bar.gapTenths > 0;
}

/**
 * Up to three distinctive styles, largest gap above the state average first.
 * A style counts when its gap is positive and its name is non-blank.
 * Unused slots stay blank on the right.
 * @param {object | null | undefined} identity
 * @returns {string[]}
 */
export function formatDistinctiveStyleCells(identity) {
  const distinctive = styleBarsForIdentity(identity)
    .filter(isListedDistinctiveStyle)
    .sort(compareDistinctiveGap);
  return DISTINCTIVE_STYLE_SLOT_INDEXES.map((slot) => formatDistinctiveStyleCell(distinctive[slot]));
}

const LEADING_STYLE_LIMIT = 3;

/**
 * Up to three groups with the largest displayed shares.
 * Ties break by group name so the set stays stable.
 * @param {{ group: string, displayPercentTenths: number }[]} bars
 * @returns {string[]}
 */
export function leadingStyleGroups(bars) {
  const ranked = (Array.isArray(bars) ? bars : [])
    .filter((bar) => bar && typeof bar.group === 'string')
    .slice()
    .sort((left, right) => {
      const shareDifference = shareTenths(right) - shareTenths(left);
      if (shareDifference !== 0) {
        return shareDifference;
      }
      return left.group.localeCompare(right.group);
    });
  return ranked.slice(0, LEADING_STYLE_LIMIT).map((bar) => bar.group);
}

/**
 * @param {{ displayPercentTenths?: number }} bar
 * @returns {number}
 */
function shareTenths(bar) {
  const tenths = Number(bar.displayPercentTenths);
  if (!Number.isFinite(tenths)) {
    return Number.NEGATIVE_INFINITY;
  }
  return tenths;
}

/**
 * Gap above the state average, in percentage points with one decimal.
 * @param {number} gapTenths
 * @returns {string}
 */
export function formatPercentagePointsAboveAverage(gapTenths) {
  const points = displayPercentFromTenths(gapTenths);
  return `${points.toFixed(1)} percentage points above the state average`;
}

/**
 * @param {{ group: string, count: number, displayPercentTenths: number, gapTenths: number | null }} bar
 * @returns {string}
 */
export function formatStyleBarLabel(bar) {
  const base = `${bar.group}, ${bar.count}, ${formatDisplayPercent(bar.displayPercentTenths)}`;
  if (Number.isFinite(bar.gapTenths)) {
    return `${base}, ${formatPercentagePointsAboveAverage(bar.gapTenths)}`;
  }
  return base;
}

/**
 * @param {object[] | null | undefined} distinctiveFocus
 * @returns {string}
 */
export function formatDistinctiveFocus(distinctiveFocus) {
  if (!Array.isArray(distinctiveFocus) || distinctiveFocus.length === 0) {
    return 'Distinctive focus: none.';
  }
  const parts = distinctiveFocus
    .filter((entry) => entry && typeof entry.group === 'string')
    .map((entry) => `${entry.group} ${entry.gapTenths} tenths`);
  if (parts.length === 0) {
    return 'Distinctive focus: none.';
  }
  return `Distinctive focus: ${parts.join('; ')}.`;
}

/**
 * @param {object | null | undefined} breadth
 * @returns {string}
 */
export function formatBreadth(breadth) {
  const represented = Number.isFinite(breadth?.representedGroupCount)
    ? breadth.representedGroupCount
    : 0;
  const leadingShare = formatDisplayPercent(breadth?.topThreeShareTenths);
  return `${represented} of ${STYLE_GROUP_ORDER.length} styles represented. Leading three are ${leadingShare} of portfolio`;
}

/**
 * @param {object | null | undefined} identity
 * @returns {{ identityId: string, explanation: string }[]}
 */
export function matchExplanations(identity) {
  if (identity?.matchEligible !== true) {
    return [];
  }
  const matches = Array.isArray(identity.matches) ? identity.matches : [];
  return matches.slice(0, 3).flatMap((match) => {
    const explanation = typeof match?.explanation === 'string' ? match.explanation.trim() : '';
    if (explanation.length === 0) {
      return [];
    }
    const identityId = typeof match.identityId === 'string' && match.identityId.length > 0
      ? match.identityId
      : explanation;
    return [{ identityId, explanation }];
  });
}

/**
 * Share-gap sentence from a stored explanation. The preamble is left off.
 * @param {unknown} explanation
 * @returns {string}
 */
export function matchShareGapText(explanation) {
  if (typeof explanation !== 'string') {
    return '';
  }
  const markerIndex = explanation.indexOf(LARGEST_SHARE_GAPS_MARKER);
  if (markerIndex < 0) {
    return '';
  }
  return explanation.slice(markerIndex).trim();
}

/**
 * @param {object | null | undefined} identity
 * @returns {boolean}
 */
export function hasEligibleStoredMatches(identity) {
  if (identity?.matchEligible !== true) {
    return false;
  }
  return Array.isArray(identity.matches) && identity.matches.length > 0;
}

/**
 * @param {string} score
 * @returns {boolean}
 */
function variationAboveSimilarLimit(score) {
  const scaled = Math.round(Number(score) * 10000);
  return scaled > SIMILAR_VARIATION_LIMIT_E4;
}

/**
 * @param {object | null | undefined} match
 * @param {object[] | null | undefined} identities
 * @returns {string}
 */
function matchPagePath(match, identities) {
  const identityId = typeof match?.identityId === 'string' ? match.identityId : '';
  if (identityId.startsWith(BREWERY_IDENTITY_PREFIX)) {
    const breweryId = identityId.slice(BREWERY_IDENTITY_PREFIX.length);
    if (breweryId.length > 0) {
      return breweryPagePath(breweryId);
    }
  }
  const matchedIdentity = findIdentity({ identities }, identityId);
  const members = memberBreweryLinks(matchedIdentity);
  if (members.length === 1) {
    return members[0].path;
  }
  return BREWERY_GROUPS_PATH;
}

/**
 * Eligible stored matches at or under 0.3 total variation.
 * @param {object | null | undefined} identity
 * @param {object[] | null | undefined} identities
 * @returns {{ identityId: string, name: string, path: string, score: string, shareGaps: string }[]}
 */
export function similarPortfolioMatchRows(identity, identities) {
  if (identity?.matchEligible !== true) {
    return [];
  }
  const matches = Array.isArray(identity.matches) ? identity.matches : [];
  return matches.flatMap((match) => {
    const score = formatTotalVariation(match?.totalVariationE10);
    if (score == null || variationAboveSimilarLimit(score)) {
      return [];
    }
    const name = typeof match.displayName === 'string' ? match.displayName : '';
    const identityId = typeof match.identityId === 'string' && match.identityId.length > 0
      ? match.identityId
      : `${name}-${score}`;
    return [{
      identityId,
      name,
      path: matchPagePath(match, identities),
      score,
      shareGaps: matchShareGapText(match?.explanation),
    }];
  });
}
